"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { stegaClean } from "@sanity/client/stega";
import type { PagePricingSchema } from "@/sanity/pages/PagePricingTemplate";
import { SectionHeading } from "../ai-features/ui";
import styles from "./pricing.module.scss";

const DRAG_THRESHOLD = 4;

export default function TestimonialsSection({
  data,
}: {
  data?: PagePricingSchema["testimonials"];
}) {
  const track = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ startX: 0, startScroll: 0, moved: false });
  const settle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const items = data?.items ?? [];
  const count = items.length;

  const nearestIndex = useCallback(() => {
    const node = track.current;
    if (!node) return 0;
    const center = node.scrollLeft + node.clientWidth / 2;
    let nearest = 0;
    let distance = Infinity;
    [...node.children].forEach((child, i) => {
      const el = child as HTMLElement;
      const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - center);
      if (d < distance) {
        distance = d;
        nearest = i;
      }
    });
    return nearest;
  }, []);

  const scrollTo = useCallback((index: number) => {
    const node = track.current;
    const card = node?.children[index] as HTMLElement | undefined;
    if (!node || !card) return;
    node.scrollTo({
      left: card.offsetLeft - (node.clientWidth - card.offsetWidth) / 2,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const node = track.current;
    if (!node) return;
    let frame = 0;
    const sync = () => {
      frame = 0;
      setActive(nearestIndex());
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };
    node.addEventListener("scroll", onScroll, { passive: true });
    sync();
    return () => {
      node.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
      if (settle.current) clearTimeout(settle.current);
    };
  }, [count, nearestIndex]);

  // Mouse drag-to-scroll. Touch keeps native scrolling + snap.
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    const node = event.currentTarget;
    drag.current = {
      startX: event.clientX,
      startScroll: node.scrollLeft,
      moved: false,
    };
    if (settle.current) clearTimeout(settle.current);
    node.setPointerCapture(event.pointerId);
    setDragging(true);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    const delta = event.clientX - drag.current.startX;
    if (!drag.current.moved && Math.abs(delta) < DRAG_THRESHOLD) return;
    drag.current.moved = true;
    event.currentTarget.scrollLeft = drag.current.startScroll - delta;
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    if (!drag.current.moved) {
      setDragging(false);
      return;
    }
    scrollTo(nearestIndex());
    // Keep snap off until the settle scroll lands, or snap would jump.
    settle.current = setTimeout(() => setDragging(false), 700);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollTo(Math.min(active + 1, count - 1));
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollTo(Math.max(active - 1, 0));
    }
  };

  if (!data || !count) return null;
  return (
    <section className={styles.testimonials}>
      <SectionHeading data={{ label: data.label, title: data.title }} />
      <div
        className={styles.track}
        ref={track}
        data-dragging={dragging}
        tabIndex={0}
        role="group"
        aria-roledescription="carousel"
        aria-label={stegaClean(data.title) || "Testimonials"}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        {items.map((item, i) => (
          <article
            key={item._key}
            className={styles.quoteCard}
            data-active={i === active}
            aria-hidden={i !== active}
          >
            <div className={styles.quoteTop}>
              <i aria-hidden="true" />
              {item.feature && <span>{item.feature}</span>}
            </div>
            <blockquote>{item.quote}</blockquote>
            <footer>
              <span>{String(i + 1).padStart(2, "0")}</span>
              <span>{item.name}</span>
            </footer>
          </article>
        ))}
      </div>
      <div className={styles.controls}>
        <button
          type="button"
          aria-label="Previous testimonial"
          disabled={active === 0}
          onClick={() => scrollTo(active - 1)}
          data-direction="prev"
        />
        <div className={styles.progress} aria-hidden="true">
          {items.map((item, i) => (
            <i key={item._key} data-active={i === active} />
          ))}
        </div>
        <button
          type="button"
          aria-label="Next testimonial"
          disabled={active === count - 1}
          onClick={() => scrollTo(active + 1)}
          data-direction="next"
        />
      </div>
    </section>
  );
}
